import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Collapse,
  Alert,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentIcon from "@mui/icons-material/Payment";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  PhoneCountryCodeMap,
} from "@kivunova/kivufrontendcommon";
import { ROUTE_ORDER_SUMMARY, ROUTE_PLANS } from "../../constants/routes";
import AnimatedContainer from "../common/AnimatedContainer";
import { PaymentMethod } from "../Payments/enums/paymentEnums";
import {
  type PaymentFieldErrors,
  validatePaymentForm,
} from "./paymentOptionsValidation";

interface PlanData {
  planId: string;
  name: string;
  price: number;
  currency: string;
  billingCycle: string;
  description?: string;
}

const PaymentOptions: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>(
    PaymentMethod.MTN_MOMO,
  );
  const [mtnPhoneCountryCode, setMtnPhoneCountryCode] = useState("+250");
  const [mtnPhoneNumber, setMtnPhoneNumber] = useState("");
  const [airtelPhoneCountryCode, setAirtelPhoneCountryCode] = useState("+250");
  const [airtelPhoneNumber, setAirtelPhoneNumber] = useState("");
  const [cardEmail, setCardEmail] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCVC, setCardCVC] = useState("");
  const [cardName, setCardName] = useState("");
  const [cardCountry, setCardCountry] = useState("RW");
  const [paymentErrors, setPaymentErrors] = useState<PaymentFieldErrors>({});

  // Get plan data from navigation state
  const planData = (location.state as { plan?: PlanData })?.plan || null;

  const validationMessages = {
    phoneRequired: "Enter a phone number.",
    phoneInvalid: "Enter a valid phone number for the selected country code.",
    emailInvalid: "Enter a valid email address.",
    cardNumberInvalid: "Enter a valid card number.",
    cardExpiryInvalid: "Enter expiry as MM / YY.",
    cardCvcInvalid: "Enter a valid CVC.",
    cardNameRequired: "Enter the name on the card.",
  };

  const handlePaymentMethodChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedMethod(event.target.value as PaymentMethod);
    setPaymentErrors({});
  };

  const handleProcessPayment = () => {
    const fieldErrors = validatePaymentForm(
      {
        selectedMethod,
        mtnPhoneCountryCode,
        mtnPhoneNumber,
        airtelPhoneCountryCode,
        airtelPhoneNumber,
        cardEmail,
        cardNumber,
        cardExpiry,
        cardCVC,
        cardName,
      },
      validationMessages,
    );
    if (fieldErrors) {
      setPaymentErrors(fieldErrors);
      return;
    }

    setPaymentErrors({
      general:
        "Payment processing is not available yet. Please contact support.",
    });
  };

  const handleBack = () => {
    if (planData) {
      navigate(ROUTE_ORDER_SUMMARY, {
        state: { plan: planData },
      });
    } else {
      navigate(ROUTE_PLANS);
    }
  };

  if (!planData) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #6f49c9 50%, #5b36b0 75%, #4c2a85 100%)",
          position: "relative",
          overflow: "hidden",
          py: 6,
          px: { xs: 2, sm: 3, md: 4 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="md">
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              No plan selected
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(PAGE_ROUTE_MEMBERSHIP_PLANS)}
              sx={{
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER },
              }}
            >
              Browse Plans
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  const totalAmount = planData.price;

  const paymentMethods = [
    {
      value: PaymentMethod.MTN_MOMO,
      label: "MTN Momo",
      icon: <AccountBalanceWalletIcon />,
      description: "Pay using MTN Mobile Money",
    },
    {
      value: PaymentMethod.AIRTEL_MONEY,
      label: "Airtel Money",
      icon: <AccountBalanceWalletIcon />,
      description: "Pay using Airtel Money",
    },
    {
      value: PaymentMethod.CREDIT_DEBIT,
      label: "Pay With Card",
      icon: <CreditCardIcon />,
      description: "Pay with Visa, Mastercard, or other cards",
    },
    {
      value: PaymentMethod.PAYPAL,
      label: "PayPal",
      icon: <PaymentIcon />,
      description: "Pay securely with your PayPal account",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #6f49c9 50%, #5b36b0 75%, #4c2a85 100%)",
        position: "relative",
        overflow: "hidden",
        py: 6,
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-30%",
          right: "-15%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
          animation: "float 20s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translate(0, 0) rotate(0deg)",
            },
            "50%": {
              transform: "translate(-30px, -30px) rotate(180deg)",
            },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-20%",
          left: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
          animation: "float 15s ease-in-out infinite reverse",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translate(0, 0) rotate(0deg)",
            },
            "50%": {
              transform: "translate(30px, 30px) rotate(-180deg)",
            },
          },
        }}
      />

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <AnimatedContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <Paper
            elevation={8}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                p: 4,
                textAlign: "center",
              }}
            >
              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: {
                    xs: "1.25rem",
                    sm: "1.60rem",
                    md: "2.65rem",
                    lg: "2.75rem",
                  },
                  lineHeight: 1.3,
                  mb: 2,
                  color: "white",
                  letterSpacing: { xs: "0.01em", md: "0.02em" },
                  textShadow: "0 2px 10px rgba(0,0,0,0.2)",
                }}
              >
                Payment Options
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                  lineHeight: 1.7,
                  color: "rgba(255, 255, 255, 0.9)",
                  fontWeight: 400,
                  opacity: 0.9,
                }}
              >
                Choose your preferred payment method
              </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ p: 4 }}>
              {/* Order Summary */}
              <Box
                sx={{
                  mb: 4,
                  p: 2,
                  bgcolor: "rgba(0,0,0,0.02)",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Plan: {planData.name}
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  Total: {planData.currency} {totalAmount.toLocaleString()}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Payment Methods */}
              <FormControl component="fieldset" fullWidth>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                  Select Payment Method
                </Typography>
                <RadioGroup
                  value={selectedMethod}
                  onChange={handlePaymentMethodChange}
                >
                  {paymentMethods.map((method) => {
                    const isSelected = selectedMethod === method.value;
                    return (
                      <Paper
                        key={method.value}
                        elevation={isSelected ? 4 : 1}
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          border: isSelected
                            ? `2px solid ${BRAND_COLOR}`
                            : "2px solid transparent",
                          transition: "all 0.3s ease",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            p: 3,
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "rgba(111, 73, 201, 0.02)",
                            },
                          }}
                          onClick={() =>
                            setSelectedMethod(method.value as PaymentMethod)
                          }
                        >
                          <FormControlLabel
                            value={method.value}
                            control={<Radio />}
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    color: BRAND_COLOR,
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {method.icon}
                                </Box>
                                <Box>
                                  <Typography variant="h6" fontWeight="bold">
                                    {method.label}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {method.description}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                            sx={{ m: 0, width: "100%" }}
                          />
                        </Box>

                        {/* Collapsible Content */}
                        <Collapse in={isSelected}>
                          <Box sx={{ px: 3, pb: 3, pt: 0 }}>
                            <Divider sx={{ mb: 3 }} />

                            {/* MTN Momo Content */}
                            {method.value === PaymentMethod.MTN_MOMO && (
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 2, fontWeight: 500 }}
                                >
                                  Enter your MTN Mobile Money phone number
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 2,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <FormControl
                                    sx={{
                                      flex: "0 0 160px",
                                      minWidth: 140,
                                    }}
                                    variant="outlined"
                                  >
                                    <InputLabel id="mtn-phone-country-code-label">
                                      Code
                                    </InputLabel>
                                    <Select
                                      value={mtnPhoneCountryCode}
                                      onChange={(e) =>
                                        setMtnPhoneCountryCode(e.target.value)
                                      }
                                      labelId="mtn-phone-country-code-label"
                                      id="mtn-phone-country-code"
                                      label="Code"
                                    >
                                      {Object.keys(PhoneCountryCodeMap).map(
                                        (key) => (
                                          <MenuItem
                                            key={key}
                                            value={
                                              PhoneCountryCodeMap[key].code
                                            }
                                          >
                                            {`${PhoneCountryCodeMap[key].flag} ${PhoneCountryCodeMap[key].code}`}
                                          </MenuItem>
                                        ),
                                      )}
                                    </Select>
                                  </FormControl>
                                  <TextField
                                    label="Phone Number"
                                    value={mtnPhoneNumber}
                                    onChange={(e) => {
                                      setMtnPhoneNumber(e.target.value);
                                      if (paymentErrors.phone) {
                                        setPaymentErrors((prev) => ({
                                          ...prev,
                                          phone: undefined,
                                        }));
                                      }
                                    }}
                                    fullWidth
                                    placeholder="782484464"
                                    error={Boolean(paymentErrors.phone)}
                                    helperText={paymentErrors.phone}
                                  />
                                </Box>
                              </Box>
                            )}

                            {/* Airtel Money Content */}
                            {method.value === PaymentMethod.AIRTEL_MONEY && (
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 2, fontWeight: 500 }}
                                >
                                  Enter your Airtel Money phone number
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 2,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <FormControl
                                    sx={{
                                      flex: "0 0 160px",
                                      minWidth: 140,
                                    }}
                                    variant="outlined"
                                  >
                                    <InputLabel id="airtel-phone-country-code-label">
                                      Code
                                    </InputLabel>
                                    <Select
                                      value={airtelPhoneCountryCode}
                                      onChange={(e) =>
                                        setAirtelPhoneCountryCode(
                                          e.target.value,
                                        )
                                      }
                                      labelId="airtel-phone-country-code-label"
                                      id="airtel-phone-country-code"
                                      label="Code"
                                    >
                                      {Object.keys(PhoneCountryCodeMap).map(
                                        (key) => (
                                          <MenuItem
                                            key={key}
                                            value={
                                              PhoneCountryCodeMap[key].code
                                            }
                                          >
                                            {`${PhoneCountryCodeMap[key].flag} ${PhoneCountryCodeMap[key].code}`}
                                          </MenuItem>
                                        ),
                                      )}
                                    </Select>
                                  </FormControl>
                                  <TextField
                                    label="Phone Number"
                                    value={airtelPhoneNumber}
                                    onChange={(e) => {
                                      setAirtelPhoneNumber(e.target.value);
                                      if (paymentErrors.phone) {
                                        setPaymentErrors((prev) => ({
                                          ...prev,
                                          phone: undefined,
                                        }));
                                      }
                                    }}
                                    fullWidth
                                    placeholder="782484464"
                                    error={Boolean(paymentErrors.phone)}
                                    helperText={paymentErrors.phone}
                                  />
                                </Box>
                              </Box>
                            )}

                            {/* Pay With Card Content */}
                            {method.value === PaymentMethod.CREDIT_DEBIT && (
                              <Box>
                                {/* Email Field */}
                                <TextField
                                  label="Email"
                                  type="email"
                                  value={cardEmail}
                                  onChange={(e) => {
                                    setCardEmail(e.target.value);
                                    if (paymentErrors.email) {
                                      setPaymentErrors((prev) => ({
                                        ...prev,
                                        email: undefined,
                                      }));
                                    }
                                  }}
                                  fullWidth
                                  sx={{ mb: 3 }}
                                  placeholder="Email"
                                  error={Boolean(paymentErrors.email)}
                                  helperText={paymentErrors.email}
                                />

                                {/* Card Information */}
                                <Box sx={{ mb: 3 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ mb: 1, fontWeight: 500 }}
                                  >
                                    Card information
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                                    <TextField
                                      placeholder="1234 1234 1234 1234"
                                      value={cardNumber}
                                      onChange={(e) => {
                                        setCardNumber(e.target.value);
                                        if (paymentErrors.cardNumber) {
                                          setPaymentErrors((prev) => ({
                                            ...prev,
                                            cardNumber: undefined,
                                          }));
                                        }
                                      }}
                                      fullWidth
                                      sx={{ flex: 2 }}
                                      error={Boolean(paymentErrors.cardNumber)}
                                      helperText={paymentErrors.cardNumber}
                                    />
                                    <TextField
                                      placeholder="MM / YY"
                                      value={cardExpiry}
                                      onChange={(e) => {
                                        setCardExpiry(e.target.value);
                                        if (paymentErrors.cardExpiry) {
                                          setPaymentErrors((prev) => ({
                                            ...prev,
                                            cardExpiry: undefined,
                                          }));
                                        }
                                      }}
                                      sx={{ flex: 1 }}
                                      error={Boolean(paymentErrors.cardExpiry)}
                                      helperText={paymentErrors.cardExpiry}
                                    />
                                    <TextField
                                      placeholder="CVC"
                                      value={cardCVC}
                                      onChange={(e) => {
                                        setCardCVC(e.target.value);
                                        if (paymentErrors.cardCvc) {
                                          setPaymentErrors((prev) => ({
                                            ...prev,
                                            cardCvc: undefined,
                                          }));
                                        }
                                      }}
                                      sx={{ flex: 1 }}
                                      error={Boolean(paymentErrors.cardCvc)}
                                      helperText={paymentErrors.cardCvc}
                                    />
                                  </Box>
                                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      VISA • Mastercard • Amex • Discover
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Name on Card */}
                                <TextField
                                  label="Name on card"
                                  value={cardName}
                                  onChange={(e) => {
                                    setCardName(e.target.value);
                                    if (paymentErrors.cardName) {
                                      setPaymentErrors((prev) => ({
                                        ...prev,
                                        cardName: undefined,
                                      }));
                                    }
                                  }}
                                  fullWidth
                                  sx={{ mb: 3 }}
                                  placeholder="Name on card"
                                  error={Boolean(paymentErrors.cardName)}
                                  helperText={paymentErrors.cardName}
                                />

                                {/* Country or Region */}
                                <TextField
                                  label="Country or region"
                                  select
                                  value={cardCountry}
                                  onChange={(e) =>
                                    setCardCountry(e.target.value)
                                  }
                                  fullWidth
                                  SelectProps={{
                                    native: true,
                                  }}
                                  sx={{ mb: 2 }}
                                >
                                  <option value="RW">Rwanda</option>
                                  <option value="UG">Uganda</option>
                                  <option value="KE">Kenya</option>
                                  <option value="TZ">Tanzania</option>
                                </TextField>
                              </Box>
                            )}

                            {/* PayPal Content */}
                            {method.value === PaymentMethod.PAYPAL && (
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  You will be redirected to PayPal to complete
                                  your payment.
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })}
                </RadioGroup>
              </FormControl>

              <Divider sx={{ my: 4 }} />

              {/* Action Buttons */}
              {paymentErrors.general ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {paymentErrors.general}
                </Alert>
              ) : null}

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  fullWidth
                  sx={{
                    borderColor: BRAND_COLOR,
                    color: BRAND_COLOR,
                    "&:hover": {
                      borderColor: BRAND_HOVER,
                      backgroundColor: "rgba(111, 73, 201, 0.04)",
                    },
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleProcessPayment}
                  fullWidth
                  sx={{
                    backgroundColor: "#936DAE",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#A682C0",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 20px rgba(147, 109, 174, 0.4)",
                    },
                    transition: "all 0.3s ease",
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: "bold",
                  }}
                >
                  Complete Payment
                </Button>
              </Box>
            </Box>
          </Paper>
        </AnimatedContainer>
      </Container>
    </Box>
  );
};

export default PaymentOptions;
